import { fuzzyFilter } from 'fuzzbunny'
import useDebounce from 'hooks/use-debounce'
import { sortBy } from 'lodash'
import { Dispatch, useMemo, useState } from 'react'

type UseSearchProps<Item> = {
  items: Item[]
  searchableFields: Array<keyof Item>
  defaultSort?: keyof Item
  delay?: number
  initialSearch?: string
}

export type FilteredItem<Item> = {
  item: Item
  score: number
  highlights: { [K in keyof Item]?: string[] }
}

type UseSearchReturn<Item> = {
  search: string
  setSearch: Dispatch<string>
  filteredItems: Array<FilteredItem<Item>>
}
export const useSearch = <Item extends Record<string, any>>({
  items = [],
  searchableFields,
  defaultSort,
  delay = 500,
  initialSearch = '',
}: UseSearchProps<Item>): UseSearchReturn<Item> => {
  const [debouncedSearch, setDebouncedSearch] = useState<string>(initialSearch)
  const [search, setSearch] =
    useState<UseSearchReturn<Item>['search']>(initialSearch)

  const filteredItems: UseSearchReturn<Item>['filteredItems'] = useMemo(() => {
    const results = fuzzyFilter(items, String(debouncedSearch), {
      fields: searchableFields,
    })

    const sorted =
      !debouncedSearch && Boolean(defaultSort)
        ? sortBy(results, (result) => result.item[defaultSort], ['asc'])
        : sortBy(results, ['scored'])

    return sorted
  }, [debouncedSearch, defaultSort, items, searchableFields])

  useDebounce(
    () => {
      setDebouncedSearch(search)
    },
    [search],
    delay,
  )

  return {
    search,
    setSearch,
    filteredItems,
  }
}
